// 写作统计：每日写作字数 + 每日目标（localStorage 为主，异步同步到服务端 user_preferences）
import { storageGet, storageSet } from './storage';

const DAILY_STATS_KEY = 'writing_daily_stats';
const DAILY_GOAL_KEY = 'writing_daily_goal';
const SERVER_STATS_PREF = 'writing_daily_stats';
const SERVER_GOAL_PREF = 'writing_daily_goal';

export function getTodayKey(): string {
  return dateKey(new Date());
}

function dateKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function getDailyMap(): Record<string, number> {
  return storageGet<Record<string, number>>(DAILY_STATS_KEY, {});
}

function setDailyMap(map: Record<string, number>): void {
  storageSet(DAILY_STATS_KEY, map);
}

/** 记录本次新增字数（仅累计正向增量；删除不计入） */
export function recordWordsWritten(delta: number): void {
  if (!delta || delta <= 0) return;
  const map = getDailyMap();
  const today = getTodayKey();
  map[today] = (map[today] ?? 0) + Math.round(delta);
  setDailyMap(map);
  syncStatsToServer();
}

/** 最近 N 天的每日字数（含今天，按日期升序） */
export function getLastNDays(n: number): Array<{ date: string; label: string; words: number }> {
  const map = getDailyMap();
  const result: Array<{ date: string; label: string; words: number }> = [];
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const label = i === 0 ? '今天' : `${d.getMonth() + 1}/${d.getDate()}`;
    result.push({ date: key, label, words: map[key] ?? 0 });
  }
  return result;
}

export function getTodayWords(): number {
  return getDailyMap()[getTodayKey()] ?? 0;
}

export function getDailyGoal(): number {
  return storageGet<number>(DAILY_GOAL_KEY, 0);
}

/** 连续写作天数：今天已写作则从今天起算，今天未写作则从昨天起算（视为"待续"） */
export function getWritingStreak(n = 90): number {
  const map = getDailyMap();
  const today = new Date();
  let streak = 0;
  const startOffset = (map[dateKey(today)] ?? 0) > 0 ? 0 : 1;
  for (let i = startOffset; i < n; i++) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    if ((map[dateKey(d)] ?? 0) > 0) streak++;
    else break;
  }
  return streak;
}

/** 近 N 天写作汇总：总字数 / 活跃天数 / 平均每日（仅活跃日）/ 达标天数 */
export function getPeriodStats(n: number, goal: number): { total: number; activeDays: number; avg: number; goalDays: number } {
  const days = getLastNDays(n);
  const activeDays = days.filter((d) => d.words > 0).length;
  const total = days.reduce((s, d) => s + d.words, 0);
  const goalDays = goal > 0 ? days.filter((d) => d.words >= goal).length : 0;
  return {
    total,
    activeDays,
    avg: activeDays > 0 ? Math.round(total / activeDays) : 0,
    goalDays,
  };
}

export function setDailyGoal(goal: number): void {
  const safe = Math.max(0, Math.floor(Number(goal) || 0));
  storageSet(DAILY_GOAL_KEY, safe);
  try {
    void window.api.setUserPreference(SERVER_GOAL_PREF, safe > 0 ? String(safe) : null).catch(() => { /* ignore */ });
  } catch { /* ignore */ }
}

/** 启动时从服务端偏好恢复统计（仅当本地无数据时，避免覆盖更新的本地记录） */
export async function loadStatsFromServer(): Promise<void> {
  try {
    const prefs = await window.api.getAllUserPreferences();
    const statsPref = prefs.find(p => p.preference_name === SERVER_STATS_PREF);
    if (statsPref?.preference_value) {
      const local = getDailyMap();
      if (Object.keys(local).length === 0) {
        try {
          const parsed = JSON.parse(statsPref.preference_value) as Record<string, number>;
          setDailyMap(parsed);
        } catch { /* ignore */ }
      }
    }
    const goalPref = prefs.find(p => p.preference_name === SERVER_GOAL_PREF);
    if (goalPref?.preference_value && storageGet<number>(DAILY_GOAL_KEY, 0) === 0) {
      const parsed = Number(goalPref.preference_value);
      if (parsed > 0) storageSet(DAILY_GOAL_KEY, parsed);
    }
  } catch { /* ignore */ }
}

let syncTimer: ReturnType<typeof setTimeout> | null = null;

function syncStatsToServer(): void {
  if (syncTimer) clearTimeout(syncTimer);
  syncTimer = setTimeout(() => {
    try {
      void window.api.setUserPreference(SERVER_STATS_PREF, JSON.stringify(getDailyMap())).catch(() => { /* ignore */ });
    } catch { /* ignore */ }
  }, 1500);
}
